import { Component, OnInit, ViewEncapsulation, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { NgSelectModule } from '@ng-select/ng-select';
import {
  IEmergencia,
  IQueryParam,
  PrioridadEmergenciaMedica,
} from 'modelos/src';
import { firstValueFrom } from 'rxjs';
import { CardHeaderComponent } from '../../../../../../componentes/card-header/card-header.component';
import { ContentCardComponent } from '../../../../../../componentes/content-card/content-card.component';
import { LoadingComponent } from '../../../../../../componentes/loading/loading.component';
import { ButtonComponent } from '../../../../../../auxiliares/button/button.component';
import { HelperService } from '../../../../../../auxiliares/servicios/helper.service';
import { Lista2Service } from '../../../../../../auxiliares/servicios/lista2.service';
import { IDatosFinalizacionEmergencia } from '../../../../../../auxiliares/servicios/http/emergencias.service';
import {
  IPresentacion,
  PRIORIDADES,
  presentacionPrioridad,
} from '../../../estado-emergencia';

export interface IFinalizarEmergenciaDialogData {
  emergencia: IEmergencia;
  guardar: (datos: IDatosFinalizacionEmergencia) => Promise<unknown>;
}

/** Un ítem del catálogo que termina en "..." o "…" pide que se lo complete. */
const PUNTOS_SUSPENSIVOS = /(\.{3}|…)\s*$/;

@Component({
  selector: 'app-finalizar-emergencia-dialog',
  templateUrl: './finalizar-emergencia-dialog.component.html',
  styleUrl: '../acciones-emergencia-dialog.scss',
  encapsulation: ViewEncapsulation.None,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatCardModule,
    MatDividerModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    NgSelectModule,
    CardHeaderComponent,
    ContentCardComponent,
    LoadingComponent,
    ButtonComponent,
  ],
})
export class FinalizarEmergenciaDialogComponent implements OnInit {
  private dialogRef = inject(MatDialogRef<FinalizarEmergenciaDialogComponent>);
  private lista2 = inject(Lista2Service);
  private helper = inject(HelperService);
  public data = inject<IFinalizarEmergenciaDialogData>(MAT_DIALOG_DATA);

  private emergencia = this.data.emergencia;
  public titulo = `Finalizar la emergencia ${this.emergencia.codigo ?? ''}`;
  public cargando = true;
  public guardando = false;

  public prioridades: {
    valor: PrioridadEmergenciaMedica;
    p: IPresentacion;
  }[] = PRIORIDADES.map((valor) => ({
    valor,
    p: presentacionPrioridad(valor),
  }));
  public aperturaLabel = presentacionPrioridad(
    this.emergencia.prioridadApertura,
  ).label;

  // Catálogos (Síntomas y Diagnósticos, de Listado de categorías)
  public sintomas: string[] = [];
  public diagnosticos: string[] = [];

  // Complementos de los ítems que terminan en "..."
  public complementosSintomas: Record<string, string> = {};
  public complementoDiagnostico = '';

  public form = new FormGroup({
    prioridadCierre: new FormControl<string | null>(
      this.emergencia.prioridadCierre ||
        this.emergencia.prioridadApertura ||
        null,
    ),
    sintomasCierre: new FormControl<string[]>([]),
    diagnostico: new FormControl<string | null>(null),
    observaciones: new FormControl<string>(''),
  });

  public requiereComplemento(texto?: string | null): boolean {
    return !!texto && PUNTOS_SUSPENSIVOS.test(texto);
  }

  public base(texto: string): string {
    return texto.replace(PUNTOS_SUSPENSIVOS, '').trim();
  }

  private completar(texto: string, complemento?: string): string {
    if (!this.requiereComplemento(texto)) return texto;
    const c = complemento?.trim();
    return c ? `${this.base(texto)} ${c}` : this.base(texto);
  }

  public get sintomasConComplemento(): string[] {
    return (this.form.value.sintomasCierre ?? []).filter((s) =>
      this.requiereComplemento(s),
    );
  }

  public elegirPrioridad(valor: PrioridadEmergenciaMedica): void {
    this.form.patchValue({ prioridadCierre: valor });
  }

  private async listarCategoria(categoria: string): Promise<string[]> {
    const query: IQueryParam = {
      filter: JSON.stringify({ categoria }),
      sort: 'nombre',
      select: 'nombre',
      unlimited: true,
      includeChildren: true,
    };
    const res = await firstValueFrom(this.lista2.listadoCategorias(query));
    return (res?.datos ?? []).map((c) => c.nombre ?? '').filter((n) => !!n);
  }

  /**
   * Restaura lo ya cargado. Un valor que salió de un ítem con "..." se separa
   * en el ítem base y su complemento.
   */
  private restaurar(
    guardado: string,
    catalogo: string[],
  ): {
    item: string;
    complemento?: string;
  } {
    if (catalogo.includes(guardado)) return { item: guardado };
    const base = catalogo.find(
      (c) => this.requiereComplemento(c) && guardado.startsWith(this.base(c)),
    );
    if (base) {
      return {
        item: base,
        complemento: guardado.slice(this.base(base).length).trim(),
      };
    }
    // Valor que no está en el catálogo: se lo agrega para no perderlo
    catalogo.push(guardado);
    return { item: guardado };
  }

  public async guardar(): Promise<void> {
    if (this.guardando) return;
    const v = this.form.value;
    const sintomasCierre = (v.sintomasCierre ?? []).map((s) =>
      this.completar(s, this.complementosSintomas[s]),
    );
    const diagnostico = v.diagnostico
      ? this.completar(v.diagnostico, this.complementoDiagnostico)
      : undefined;
    this.guardando = true;
    try {
      await this.data.guardar({
        prioridadCierre: v.prioridadCierre || undefined,
        sintomasCierre: sintomasCierre.length ? sintomasCierre : undefined,
        diagnostico,
        observaciones: v.observaciones?.trim() || undefined,
      });
      this.dialogRef.close(true);
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
    this.guardando = false;
  }

  public cerrar(): void {
    this.dialogRef.close(false);
  }

  async ngOnInit(): Promise<void> {
    try {
      const [sintomas, diagnosticos] = await Promise.all([
        this.listarCategoria('Sintomas'),
        this.listarCategoria('Diagnosticos'),
      ]);
      this.sintomas = sintomas;
      this.diagnosticos = diagnosticos;

      // Síntomas: los de cierre si ya había, si no los de apertura
      const med = this.emergencia.emergenciaMedica;
      const previos = med?.sintomasCierre?.length
        ? med.sintomasCierre
        : (med?.sintomasApertura ?? []);
      const seleccionados: string[] = [];
      for (const s of previos) {
        const r = this.restaurar(s, this.sintomas);
        seleccionados.push(r.item);
        if (r.complemento) this.complementosSintomas[r.item] = r.complemento;
      }
      this.sintomas = [...this.sintomas];

      let diagnostico: string | null = null;
      if (med?.diagnostico) {
        const r = this.restaurar(med.diagnostico, this.diagnosticos);
        diagnostico = r.item;
        this.complementoDiagnostico = r.complemento ?? '';
        this.diagnosticos = [...this.diagnosticos];
      }
      this.form.patchValue({ sintomasCierre: seleccionados, diagnostico });
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
    this.cargando = false;
  }
}
